'use client'

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { cn } from '@/utilities/ui'

export type ScrollFadeEdge = 'top' | 'bottom' | 'left' | 'right'

export const EMPTY_VERTICAL_SCROLL_AFFORDANCE = {
  canScrollDown: false,
  canScrollUp: false,
  overflows: false,
  scrollTop: 0,
  scrollHeight: 0,
  clientHeight: 0,
}

export type VerticalScrollAffordance = typeof EMPTY_VERTICAL_SCROLL_AFFORDANCE

export const EMPTY_HORIZONTAL_SCROLL_AFFORDANCE = {
  canScrollLeft: false,
  canScrollRight: false,
  overflows: false,
  scrollLeft: 0,
  scrollWidth: 0,
  clientWidth: 0,
}

export type HorizontalScrollAffordance = typeof EMPTY_HORIZONTAL_SCROLL_AFFORDANCE

const SCROLL_FADE_CLASS_NAMES: Record<ScrollFadeEdge, string> = {
  top: 'inset-x-0 top-0 h-6 rounded-t-lg bg-gradient-to-b from-white via-white/90 to-white/0',
  bottom: 'inset-x-0 bottom-0 h-6 rounded-b-lg bg-gradient-to-t from-white via-white/90 to-white/0',
  left: 'inset-y-0 left-0 w-6 rounded-l-lg bg-gradient-to-r from-white via-white/90 to-white/0',
  right: 'inset-y-0 right-0 w-6 rounded-r-lg bg-gradient-to-l from-white via-white/90 to-white/0',
}

export function ScrollFade({
  edge,
  testId,
  visible,
}: Readonly<{ edge: ScrollFadeEdge; testId: string; visible: boolean }>) {
  return visible ? (
    <div
      aria-hidden="true"
      className={cn('pointer-events-none absolute', SCROLL_FADE_CLASS_NAMES[edge])}
      data-testid={testId}
    />
  ) : null
}

export function getVerticalScrollAffordance(
  element: HTMLDivElement | null,
): VerticalScrollAffordance {
  if (element) {
    const maxScrollTop = Math.max(0, element.scrollHeight - element.clientHeight)
    return {
      canScrollDown: element.scrollTop < maxScrollTop - 1,
      canScrollUp: element.scrollTop > 1,
      overflows: maxScrollTop > 1,
      scrollTop: element.scrollTop,
      scrollHeight: element.scrollHeight,
      clientHeight: element.clientHeight,
    }
  }
  return EMPTY_VERTICAL_SCROLL_AFFORDANCE
}

export function getHorizontalScrollAffordance(
  element: HTMLDivElement | null,
): HorizontalScrollAffordance {
  if (element) {
    const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth)
    return {
      canScrollLeft: element.scrollLeft > 1,
      canScrollRight: element.scrollLeft < maxScrollLeft - 1,
      overflows: maxScrollLeft > 1,
      scrollLeft: element.scrollLeft,
      scrollWidth: element.scrollWidth,
      clientWidth: element.clientWidth,
    }
  }
  return EMPTY_HORIZONTAL_SCROLL_AFFORDANCE
}

export function PersistentHorizontalScrollbar({
  scrollRef,
  affordance,
  testId,
  controlsId,
}: Readonly<{
  scrollRef: RefObject<HTMLDivElement | null>
  affordance: HorizontalScrollAffordance
  testId: string
  controlsId?: string
}>) {
  const scrollToClientX = useCallback(
    (clientX: number, track: HTMLDivElement) => {
      const scrollElement = scrollRef.current
      if (!scrollElement || affordance.scrollWidth <= 0) return

      const rect = track.getBoundingClientRect()
      if (rect.width <= 0) return

      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
      const maxScrollLeft = Math.max(0, affordance.scrollWidth - affordance.clientWidth)
      scrollElement.scrollLeft = ratio * maxScrollLeft
    },
    [affordance.clientWidth, affordance.scrollWidth, scrollRef],
  )

  if (!affordance.overflows || affordance.scrollWidth <= 0) {
    return null
  }

  const thumbWidthPercent = (affordance.clientWidth / affordance.scrollWidth) * 100
  const thumbOffsetPercent = (affordance.scrollLeft / affordance.scrollWidth) * 100
  const maxScrollLeft = Math.max(0, affordance.scrollWidth - affordance.clientWidth)
  const valueNow =
    maxScrollLeft === 0 ? 0 : Math.round((affordance.scrollLeft / maxScrollLeft) * 100)

  return (
    <div
      role="scrollbar"
      aria-orientation="horizontal"
      aria-controls={controlsId}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={valueNow}
      data-testid={testId}
      className="h-2.5 w-full cursor-pointer border-t border-light-grey bg-light-purple"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId)
        scrollToClientX(event.clientX, event.currentTarget)
      }}
      onPointerMove={(event) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
        scrollToClientX(event.clientX, event.currentTarget)
      }}
    >
      <div
        aria-hidden="true"
        className="h-full rounded-full bg-bright-blue/40"
        style={{
          width: `${thumbWidthPercent}%`,
          marginLeft: `${thumbOffsetPercent}%`,
        }}
      />
    </div>
  )
}

export function PersistentVerticalScrollbar({
  scrollRef,
  affordance,
  testId,
  controlsId,
}: Readonly<{
  scrollRef: RefObject<HTMLDivElement | null>
  affordance: VerticalScrollAffordance
  testId: string
  controlsId?: string
}>) {
  const scrollToClientY = useCallback(
    (clientY: number, track: HTMLDivElement) => {
      const scrollElement = scrollRef.current
      if (!scrollElement || affordance.scrollHeight <= 0) return

      const rect = track.getBoundingClientRect()
      if (rect.height <= 0) return

      const maxScrollTop = Math.max(0, affordance.scrollHeight - affordance.clientHeight)
      if (maxScrollTop <= 0) return

      const thumbHeight = Math.max(
        (affordance.clientHeight / affordance.scrollHeight) * rect.height,
        24,
      )
      const maxThumbTravel = Math.max(1, rect.height - thumbHeight)
      const thumbTop = Math.min(maxThumbTravel, Math.max(0, clientY - rect.top - thumbHeight / 2))
      scrollElement.scrollTop = (thumbTop / maxThumbTravel) * maxScrollTop
    },
    [affordance.clientHeight, affordance.scrollHeight, scrollRef],
  )

  if (!affordance.overflows || affordance.scrollHeight <= 0) {
    return null
  }

  const thumbHeightPercent = Math.max((affordance.clientHeight / affordance.scrollHeight) * 100, 12)
  const maxThumbTravelPercent = Math.max(0, 100 - thumbHeightPercent)
  const thumbOffsetPercent =
    affordance.scrollHeight <= affordance.clientHeight
      ? 0
      : (affordance.scrollTop / (affordance.scrollHeight - affordance.clientHeight)) *
        maxThumbTravelPercent
  const maxScrollTop = Math.max(0, affordance.scrollHeight - affordance.clientHeight)
  const valueNow = maxScrollTop === 0 ? 0 : Math.round((affordance.scrollTop / maxScrollTop) * 100)

  return (
    <div
      role="scrollbar"
      aria-orientation="vertical"
      aria-controls={controlsId}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={valueNow}
      data-testid={testId}
      className={cn(
        'relative w-3 shrink-0 self-stretch cursor-pointer',
        'border-l border-cp-grey bg-cp-light-purple',
      )}
      onPointerDown={(event) => {
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        scrollToClientY(event.clientY, event.currentTarget)
      }}
      onPointerMove={(event) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
        event.preventDefault()
        scrollToClientY(event.clientY, event.currentTarget)
      }}
    >
      <div
        aria-hidden="true"
        data-testid={`${testId}-thumb`}
        className="absolute inset-x-0.5 rounded-full bg-cp-bright-blue/50"
        style={{
          height: `${thumbHeightPercent}%`,
          top: `${thumbOffsetPercent}%`,
        }}
      />
    </div>
  )
}

export function useHorizontalScrollAffordance(contentDeps: readonly unknown[] = []): {
  scrollRef: RefObject<HTMLDivElement | null>
  affordance: HorizontalScrollAffordance
  updateAffordance: () => void
} {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [affordance, setAffordance] = useState<HorizontalScrollAffordance>(
    EMPTY_HORIZONTAL_SCROLL_AFFORDANCE,
  )

  const updateAffordance = useCallback(() => {
    const next = getHorizontalScrollAffordance(scrollRef.current)
    setAffordance((previous) =>
      previous.canScrollLeft === next.canScrollLeft &&
      previous.canScrollRight === next.canScrollRight &&
      previous.overflows === next.overflows &&
      previous.scrollLeft === next.scrollLeft &&
      previous.scrollWidth === next.scrollWidth &&
      previous.clientWidth === next.clientWidth
        ? previous
        : next,
    )
  }, [])

  const contentDepsKey = JSON.stringify(contentDeps)

  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    updateAffordance()
    scrollElement.addEventListener('scroll', updateAffordance, { passive: true })

    const resizeObserver = new ResizeObserver(() => {
      updateAffordance()
    })
    resizeObserver.observe(scrollElement)
    const scrollContent = scrollElement.firstElementChild
    if (scrollContent) {
      resizeObserver.observe(scrollContent)
    }

    return () => {
      scrollElement.removeEventListener('scroll', updateAffordance)
      resizeObserver.disconnect()
    }
  }, [contentDepsKey, updateAffordance])

  return { scrollRef, affordance, updateAffordance }
}

export function useVerticalScrollAffordance(contentDeps: readonly unknown[] = []): {
  scrollRef: RefObject<HTMLDivElement | null>
  affordance: VerticalScrollAffordance
  updateAffordance: () => void
} {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [affordance, setAffordance] = useState<VerticalScrollAffordance>(
    EMPTY_VERTICAL_SCROLL_AFFORDANCE,
  )

  const updateAffordance = useCallback(() => {
    const next = getVerticalScrollAffordance(scrollRef.current)
    setAffordance((previous) =>
      previous.canScrollDown === next.canScrollDown &&
      previous.canScrollUp === next.canScrollUp &&
      previous.overflows === next.overflows &&
      previous.scrollTop === next.scrollTop &&
      previous.scrollHeight === next.scrollHeight &&
      previous.clientHeight === next.clientHeight
        ? previous
        : next,
    )
  }, [])

  const contentDepsKey = JSON.stringify(contentDeps)

  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    updateAffordance()
    scrollElement.addEventListener('scroll', updateAffordance, { passive: true })

    const resizeObserver = new ResizeObserver(() => {
      updateAffordance()
    })
    resizeObserver.observe(scrollElement)
    const scrollContent = scrollElement.firstElementChild
    if (scrollContent) {
      resizeObserver.observe(scrollContent)
    }

    return () => {
      scrollElement.removeEventListener('scroll', updateAffordance)
      resizeObserver.disconnect()
    }
  }, [contentDepsKey, updateAffordance])

  return { scrollRef, affordance, updateAffordance }
}
